import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Plus, Shield, Trash2, Loader2, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import {
  GET_IP_WHITELIST,
  ADD_IP_WHITELIST_ENTRY,
  DELETE_IP_WHITELIST_ENTRY,
  type GetIpWhitelistData,
  type GetIpWhitelistVars,
} from '@/graphql/ipWhitelist';

interface IpWhitelistCardProps {
  clientId: string;
}

const PAGE_SIZE = 10;

/**
 * Enforcement mode display config.
 * Slope displays the mode as read-only — only BLAST admins can change it.
 */
const ENFORCEMENT_MODE_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  monitor: {
    label: 'Monitor',
    color: 'text-rust dark:text-salmon',
    bgColor: 'bg-rust/10 dark:bg-salmon/10',
  },
  mfa_enforce: {
    label: 'MFA Enforce',
    color: 'text-harvest dark:text-harvest-light',
    bgColor: 'bg-harvest/10 dark:bg-harvest-light/10',
  },
  block: {
    label: 'Block',
    color: 'text-sage dark:text-sage-light',
    bgColor: 'bg-sage/10 dark:bg-sage-light/10',
  },
};

// ===================================================================
// IP Validation (public IPs only, IPv4/IPv6 with CIDR)
// ===================================================================

const isPrivateIpv4 = (ip: string): boolean => {
  const parts = ip.split('.');
  if (parts.length !== 4) return false;
  const first = parseInt(parts[0]);
  const second = parseInt(parts[1]);
  if (first === 10) return true;
  if (first === 172 && second >= 16 && second <= 31) return true;
  if (first === 192 && second === 168) return true;
  if (first === 127) return true;
  return false;
};

const isPrivateIpv6 = (ip: string): boolean => {
  const normalized = ip.toLowerCase();
  if (normalized.startsWith('fc') || normalized.startsWith('fd')) return true;
  if (normalized.startsWith('fe80')) return true;
  if (normalized === '::1' || normalized === '0:0:0:0:0:0:0:1') return true;
  return false;
};

const validateCidr = (address: string): { valid: boolean; type?: 'ipv4' | 'ipv6'; error?: string } => {
  const [ip, cidr] = address.split('/');

  // IPv4
  const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (ipv4Regex.test(ip)) {
    const parts = ip.split('.').map(Number);
    if (parts.some((p) => p > 255)) return { valid: false, error: 'Invalid IPv4 address format' };
    if (isPrivateIpv4(ip)) return { valid: false, error: 'Private IP addresses are not allowed' };
    if (cidr && (parseInt(cidr) < 0 || parseInt(cidr) > 32)) return { valid: false, error: 'Invalid CIDR notation for IPv4 (0-32)' };
    return { valid: true, type: 'ipv4' };
  }

  // IPv6
  const ipv6Regex = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}$/;
  if (ipv6Regex.test(ip) || ip.includes('::')) {
    if (isPrivateIpv6(ip)) return { valid: false, error: 'Private IP addresses are not allowed' };
    if (cidr && (parseInt(cidr) < 0 || parseInt(cidr) > 128)) return { valid: false, error: 'Invalid CIDR notation for IPv6 (0-128)' };
    return { valid: true, type: 'ipv6' };
  }

  return { valid: false, error: 'Invalid IP address format' };
};

/**
 * Detect IP type from a CIDR string for display badges
 */
const detectIpType = (cidr: string): 'ipv4' | 'ipv6' => {
  return cidr.includes(':') ? 'ipv6' : 'ipv4';
};

export function IpWhitelistCard({ clientId }: IpWhitelistCardProps) {
  // ==================== Pagination State ====================
  const [currentPage, setCurrentPage] = useState(1);

  // ==================== GraphQL ====================
  const { data, loading, error } = useQuery<GetIpWhitelistData, GetIpWhitelistVars>(
    GET_IP_WHITELIST,
    { variables: { organizationId: clientId, page: currentPage, pageSize: PAGE_SIZE } }
  );

  const [addEntry, { loading: isAdding }] = useMutation(ADD_IP_WHITELIST_ENTRY, {
    refetchQueries: [{ query: GET_IP_WHITELIST, variables: { organizationId: clientId, page: currentPage, pageSize: PAGE_SIZE } }],
  });

  const [deleteEntry, { loading: isDeleting }] = useMutation(DELETE_IP_WHITELIST_ENTRY, {
    refetchQueries: [{ query: GET_IP_WHITELIST, variables: { organizationId: clientId, page: currentPage, pageSize: PAGE_SIZE } }],
  });

  // ==================== State ====================
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [cidrInput, setCidrInput] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [cidrError, setCidrError] = useState('');

  // ==================== Derived ====================
  const whitelist = data?.ipWhitelist;
  const entries = whitelist?.entries ?? [];
  const totalEntries = whitelist?.totalEntries ?? 0;
  const totalPages = Math.max(Math.ceil(totalEntries / PAGE_SIZE), 1);
  const enforcementMode = whitelist?.enforcementMode ?? 'monitor';
  const modeConfig = ENFORCEMENT_MODE_CONFIG[enforcementMode] ?? ENFORCEMENT_MODE_CONFIG.monitor;
  const deleteTarget = entries.find((e) => e.id === deleteConfirmId);

  // ==================== Handlers ====================
  const resetForm = () => {
    setCidrInput('');
    setDescriptionInput('');
    setCidrError('');
  };

  const handleAdd = async () => {
    const validation = validateCidr(cidrInput);
    if (!validation.valid) {
      setCidrError(validation.error || 'Invalid CIDR');
      return;
    }

    try {
      await addEntry({
        variables: { organizationId: clientId, cidr: cidrInput, description: descriptionInput || undefined },
      });
      setShowAddModal(false);
      resetForm();
      // Jump to last page to see the newly added entry
      const newTotal = totalEntries + 1;
      const lastPage = Math.ceil(newTotal / PAGE_SIZE);
      setCurrentPage(lastPage);
    } catch (err) {
      console.error('Failed to add IP whitelist entry:', err);
      setCidrError('Failed to add entry. Please try again.');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await deleteEntry({
        variables: { organizationId: clientId, entryId: deleteConfirmId },
      });
      setDeleteConfirmId(null);
      // If we deleted the last item on the current page, go back a page
      if (entries.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (err) {
      console.error('Failed to delete IP whitelist entry:', err);
    }
  };

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  // ==================== Loading / Error ====================
  if (loading) {
    return (
      <div className="card">
        <div className="card-body flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-chalkboard dark:text-rain-cloud" />
          <span className="ml-2 text-chalkboard dark:text-rain-cloud">Loading IP Whitelist...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="card-body flex items-center justify-center py-12 text-rust dark:text-salmon">
          <AlertCircle className="w-5 h-5 mr-2" />
          Failed to load IP Whitelist
        </div>
      </div>
    );
  }

  // ==================== Render ====================
  return (
    <>
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-turquoise dark:text-aqua" />
                <h2 className="text-lg font-semibold text-slate dark:text-mist">IP Whitelist</h2>
                {/* Read-only enforcement mode badge */}
                <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', modeConfig.bgColor, modeConfig.color)}>
                  {modeConfig.label}
                </span>
              </div>
              <p className="text-sm text-chalkboard dark:text-rain-cloud mt-1">
                Manage allowed public IP addresses for BLAST access (IPv4 and IPv6 with CIDR)
              </p>
            </div>
            <button
              onClick={() => { resetForm(); setShowAddModal(true); }}
              className="btn-primary"
            >
              <Plus className="w-4 h-4" />
              Add Entry
            </button>
          </div>
        </div>
        <div className="card-body p-0">
          <table className="table">
            <thead>
              <tr>
                <th>CIDR Block</th>
                <th>Type</th>
                <th>Description</th>
                <th className="w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                const ipType = detectIpType(entry.cidr);
                return (
                  <tr key={entry.id} className="group">
                    <td className="font-mono text-sm text-slate dark:text-mist">{entry.cidr}</td>
                    <td>
                      <span className={cn(
                        'badge',
                        ipType === 'ipv4' ? 'badge-primary' : 'bg-violet/10 text-violet dark:bg-violet-light/20 dark:text-violet-light'
                      )}>
                        {ipType.toUpperCase()}
                      </span>
                    </td>
                    <td className="text-chalkboard dark:text-rain-cloud">{entry.description || '—'}</td>
                    <td>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setDeleteConfirmId(entry.id)}
                          className="btn-ghost btn-icon btn-sm text-rust dark:text-salmon"
                          title="Delete entry"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {entries.length === 0 && (
            <div className="p-8 text-center text-chalkboard dark:text-rain-cloud">
              No IP entries yet. Click &quot;Add Entry&quot; to add an allowed IP range.
            </div>
          )}

          {/* Pagination Controls */}
          {totalEntries > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-pale-gray-3 dark:border-night-pond">
              <p className="text-sm text-chalkboard dark:text-rain-cloud">
                Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, totalEntries)} of {totalEntries} entries
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="btn-ghost btn-icon btn-sm disabled:opacity-30"
                    title="Previous page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => goToPage(page)}
                      className={cn(
                        'btn-ghost btn-sm min-w-[2rem] text-center',
                        page === currentPage
                          ? 'bg-turquoise/10 text-turquoise dark:bg-aqua/10 dark:text-aqua font-semibold'
                          : ''
                      )}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="btn-ghost btn-icon btn-sm disabled:opacity-30"
                    title="Next page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Entry Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); resetForm(); }}
        title="Add IP Whitelist Entry"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              CIDR Block
            </label>
            <input
              type="text"
              value={cidrInput}
              onChange={(e) => { setCidrInput(e.target.value); setCidrError(''); }}
              placeholder="e.g., 203.0.113.0/24 or 2001:db8::/32"
              className={cn('input font-mono', cidrError && 'border-rust dark:border-salmon')}
            />
            {cidrError && (
              <p className="text-sm text-rust dark:text-salmon mt-1">{cidrError}</p>
            )}
            <p className="text-xs text-chalkboard dark:text-rain-cloud mt-1">
              Supports IPv4, IPv6, and CIDR notation. Private IP addresses are not allowed.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Description
            </label>
            <input
              type="text"
              value={descriptionInput}
              onChange={(e) => setDescriptionInput(e.target.value)}
              placeholder="e.g., Main Office, VPN Range"
              className="input"
              maxLength={255}
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => { setShowAddModal(false); resetForm(); }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              className="btn-primary"
              disabled={!cidrInput || isAdding}
            >
              {isAdding ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Entry'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        title="Delete IP Whitelist Entry"
      >
        <div className="space-y-4">
          <p className="text-slate dark:text-mist">
            Are you sure you want to remove{' '}
            <span className="font-mono font-semibold">{deleteTarget?.cidr}</span>{' '}
            from the IP whitelist?
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setDeleteConfirmId(null)} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="btn-primary bg-rust hover:bg-rust/90"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Delete
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
